import { Aurora } from "@/components/Aurora";

/* ------------------------------ Shared shell ------------------------------ */
const Shell = ({ headline, children, chips }) => (
  <div className="relative w-full">
    <div className="absolute -inset-6 rounded-[2.5rem] bg-breeze-gradient opacity-30 blur-3xl" aria-hidden="true" />
    <div className="relative rounded-[2rem] overflow-hidden bg-[#0B132B] text-white border border-white/10 shadow-2xl shadow-[#0B132B]/40">
      <div className="relative">
        <Aurora intensity="intense" />
        <div className="relative z-10 p-6 md:p-8">
          <div className="mb-5">
            <span className="inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur ring-1 ring-white/15 px-3.5 py-1.5 text-sm font-semibold text-white">
              <span className="w-2 h-2 rounded-full bg-[#29ABE2]" />
              {headline}
            </span>
          </div>
          {children}
          <div className="flex flex-wrap gap-2 mt-5">
            {chips.map((c, i) => (
              <span
                key={c}
                className={`rounded-full px-3.5 py-1.5 ring-1 text-sm font-semibold ${
                  i === 0
                    ? "bg-[#29ABE2]/15 text-[#4CC3F0] ring-[#29ABE2]/30"
                    : "bg-white/10 text-white ring-white/15"
                }`}
              >
                {c}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  </div>
);

const sharedDefs = (
  <defs>
    <linearGradient id="pmg-line" x1="0" x2="1">
      <stop offset="0%" stopColor="#4CC3F0" />
      <stop offset="100%" stopColor="#0a6fc2" />
    </linearGradient>
    <linearGradient id="pmg-fill" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stopColor="#29ABE2" stopOpacity="0.4" />
      <stop offset="100%" stopColor="#29ABE2" stopOpacity="0" />
    </linearGradient>
    <radialGradient id="pmg-radial" cx="50%" cy="50%" r="50%">
      <stop offset="0%" stopColor="#29ABE2" stopOpacity="0.5" />
      <stop offset="100%" stopColor="#29ABE2" stopOpacity="0" />
    </radialGradient>
    <filter id="pmg-glow" x="-50%" y="-50%" width="200%" height="200%">
      <feGaussianBlur stdDeviation="2" />
    </filter>
  </defs>
);

/* ============================== FIA — Lock-in =========================== */
export const FIAMotionGraphic = () => {
  const steps = [
    { x: 40, y: 200, w: 50, h: 30, delay: 0.4 },
    { x: 105, y: 170, w: 50, h: 60, delay: 0.7 },
    { x: 170, y: 135, w: 50, h: 95, delay: 1.0 },
    { x: 235, y: 105, w: 50, h: 125, delay: 1.3 },
    { x: 300, y: 70, w: 50, h: 160, delay: 1.6 },
  ];
  return (
    <Shell
      headline="Lock in your gains"
      chips={["Locked-in growth", "Tax-deferred", "Income for life"]}
    >
      <svg viewBox="0 0 400 260" className="w-full h-auto block" aria-hidden="true">
        {sharedDefs}
        <line x1="10" y1="230" x2="390" y2="230" stroke="#29ABE2" strokeWidth="1.2" strokeDasharray="5 5" opacity="0.55" />
        <text x="14" y="252" fill="#29ABE2" fontSize="16" fontWeight="800">
          Principal protected
        </text>

        {/* dotted volatility line up top */}
        <path
          d="M 10 70 L 50 50 L 90 95 L 140 45 L 190 85 L 240 40 L 290 80 L 340 35 L 390 70"
          fill="none"
          stroke="rgba(255,255,255,0.28)"
          strokeWidth="1.4"
          strokeDasharray="4 6"
          style={{ strokeDasharray: 700, strokeDashoffset: 700, animation: "iul-draw 2s ease-out 0.2s forwards" }}
        />
        <text x="14" y="30" fill="rgba(255,255,255,0.55)" fontSize="15" fontWeight="700">
          Market swings
        </text>

        {/* stepped bars */}
        {steps.map((s, i) => (
          <g key={i} style={{ opacity: 0, animation: `iul-fade 0.5s ease-out ${s.delay}s forwards` }}>
            <rect
              x={s.x}
              y={s.y}
              width={s.w}
              height={s.h}
              rx="8"
              fill="url(#pmg-fill)"
              stroke="url(#pmg-line)"
              strokeWidth="1.8"
            />
          </g>
        ))}

        {/* callout "↓ Locked-in gains" — above bars in empty space */}
        <g style={{ opacity: 0, animation: "iul-fade 0.8s ease-out 1.9s forwards" }}>
          <text x="215" y="115" fill="#4CC3F0" fontSize="15" fontWeight="800">
            ↓ Locked-in gains
          </text>
        </g>

        {/* pulse on top bar */}
        <circle cx="325" cy="70" r="5" fill="#fff" style={{ opacity: 0, animation: "iul-fade 0.5s ease-out 1.8s forwards" }} />
        <circle cx="325" cy="70" r="5" fill="#29ABE2">
          <animate attributeName="r" values="5;13;5" dur="2.8s" repeatCount="indefinite" begin="2s" />
          <animate attributeName="opacity" values="0.6;0;0.6" dur="2.8s" repeatCount="indefinite" begin="2s" />
        </circle>
      </svg>
    </Shell>
  );
};

/* ============================== Term — Defined window =================== */
export const TermMotionGraphic = () => {
  return (
    <Shell
      headline="Protection for the years that matter"
      chips={["Pure protection", "Affordable", "Locked-in term"]}
    >
      <svg viewBox="0 0 400 260" className="w-full h-auto block" aria-hidden="true">
        {sharedDefs}

        {/* horizon line */}
        <line x1="10" y1="215" x2="390" y2="215" stroke="rgba(255,255,255,0.22)" strokeWidth="1.2" />

        {/* tick marks */}
        {[40, 110, 180, 250, 320, 390].map((x, i) => (
          <line key={i} x1={x} y1="215" x2={x} y2="225" stroke="rgba(255,255,255,0.4)" strokeWidth="1.2" />
        ))}
        <text x="14" y="248" fill="#29ABE2" fontSize="16" fontWeight="800">
          Coverage period
        </text>

        {/* dome */}
        <path
          d="M 40 215 Q 200 35, 360 215"
          fill="url(#pmg-fill)"
          stroke="url(#pmg-line)"
          strokeWidth="2.5"
          strokeLinecap="round"
          filter="url(#pmg-glow)"
          style={{ strokeDasharray: 600, strokeDashoffset: 600, animation: "iul-draw 2s ease-out 0.3s forwards" }}
        />
        <path
          d="M 40 215 Q 200 35, 360 215"
          fill="none"
          stroke="url(#pmg-line)"
          strokeWidth="2.5"
          strokeLinecap="round"
          style={{ strokeDasharray: 600, strokeDashoffset: 600, animation: "iul-draw 2s ease-out 0.3s forwards" }}
        />

        {/* family */}
        <g style={{ opacity: 0, animation: "iul-fade 0.8s ease-out 1.4s forwards" }}>
          <circle cx="170" cy="175" r="11" fill="#4CC3F0" />
          <path d="M 154 215 Q 154 185, 170 185 Q 186 185, 186 215 Z" fill="#4CC3F0" />
          <circle cx="205" cy="175" r="11" fill="#fff" opacity="0.92" />
          <path d="M 189 215 Q 189 185, 205 185 Q 221 185, 221 215 Z" fill="#fff" opacity="0.92" />
          <circle cx="235" cy="187" r="8" fill="#4CC3F0" opacity="0.9" />
          <path d="M 223 215 Q 223 194, 235 194 Q 247 194, 247 215 Z" fill="#4CC3F0" opacity="0.9" />
        </g>

        {/* big "Your family, covered" caption */}
        <g style={{ opacity: 0, animation: "iul-fade 0.8s ease-out 1.8s forwards" }}>
          <text x="200" y="100" fill="#fff" fontSize="17" fontWeight="800" textAnchor="middle">
            Your family, covered
          </text>
        </g>

        {/* dome edge labels */}
        <g style={{ opacity: 0, animation: "iul-fade 0.8s ease-out 1.6s forwards" }}>
          <text x="14" y="208" fill="rgba(255,255,255,0.7)" fontSize="13" fontWeight="700">
            Start
          </text>
          <text x="365" y="208" fill="rgba(255,255,255,0.7)" fontSize="13" fontWeight="700">
            End
          </text>
        </g>

        {/* dome endpoint pulses */}
        <circle cx="40" cy="215" r="5" fill="#29ABE2">
          <animate attributeName="r" values="5;11;5" dur="3s" repeatCount="indefinite" />
          <animate attributeName="opacity" values="0.6;0;0.6" dur="3s" repeatCount="indefinite" />
        </circle>
        <circle cx="360" cy="215" r="5" fill="#29ABE2">
          <animate attributeName="r" values="5;11;5" dur="3s" repeatCount="indefinite" begin="1.5s" />
          <animate attributeName="opacity" values="0.6;0;0.6" dur="3s" repeatCount="indefinite" begin="1.5s" />
        </circle>
      </svg>
    </Shell>
  );
};

/* ============================== FEX — Simple, warm ====================== */
export const FEXMotionGraphic = () => {
  return (
    <Shell
      headline="A final gift for your family"
      chips={["Easy approval", "Family covered", "Peace of mind"]}
    >
      <svg viewBox="0 0 400 260" className="w-full h-auto block" aria-hidden="true">
        {sharedDefs}

        <line x1="10" y1="225" x2="390" y2="225" stroke="rgba(255,255,255,0.22)" strokeWidth="1.2" />

        {/* glow */}
        <circle cx="200" cy="155" r="115" fill="url(#pmg-radial)" />

        {/* dome */}
        <path
          d="M 80 165 Q 200 50, 320 165"
          fill="url(#pmg-fill)"
          stroke="url(#pmg-line)"
          strokeWidth="2.8"
          strokeLinecap="round"
          style={{ strokeDasharray: 500, strokeDashoffset: 500, animation: "iul-draw 1.8s ease-out 0.3s forwards" }}
        />
        <line x1="200" y1="50" x2="200" y2="40" stroke="url(#pmg-line)" strokeWidth="3" strokeLinecap="round" />
        <circle cx="200" cy="38" r="5" fill="#4CC3F0" />

        {/* heart */}
        <g
          transform="translate(200 115)"
          style={{ opacity: 0, animation: "iul-fade 0.8s ease-out 1.2s forwards" }}
        >
          <g>
            <path
              d="M0 -4 C -9 -18, -24 -8, -24 5 C -24 18, -8 26, 0 34 C 8 26, 24 18, 24 5 C 24 -8, 9 -18, 0 -4 Z"
              fill="url(#pmg-line)"
              filter="url(#pmg-glow)"
            />
            <path
              d="M0 -4 C -9 -18, -24 -8, -24 5 C -24 18, -8 26, 0 34 C 8 26, 24 18, 24 5 C 24 -8, 9 -18, 0 -4 Z"
              fill="none"
              stroke="#fff"
              strokeWidth="1.1"
              opacity="0.6"
            />
            <animateTransform
              attributeName="transform"
              type="scale"
              values="1;1.08;1"
              dur="1.8s"
              repeatCount="indefinite"
              additive="sum"
            />
          </g>
        </g>

        {/* family of 3 */}
        <g style={{ opacity: 0, animation: "iul-fade 0.8s ease-out 1.6s forwards" }}>
          <circle cx="160" cy="178" r="12" fill="#fff" opacity="0.94" />
          <path d="M 143 225 Q 143 189, 160 189 Q 177 189, 177 225 Z" fill="#fff" opacity="0.94" />
          <circle cx="240" cy="178" r="12" fill="#4CC3F0" />
          <path d="M 223 225 Q 223 189, 240 189 Q 257 189, 257 225 Z" fill="#4CC3F0" />
          <circle cx="200" cy="190" r="9" fill="#4CC3F0" opacity="0.88" />
          <path d="M 187 225 Q 187 198, 200 198 Q 213 198, 213 225 Z" fill="#4CC3F0" opacity="0.88" />
        </g>

        {/* caption */}
        <g style={{ opacity: 0, animation: "iul-fade 0.8s ease-out 2s forwards" }}>
          <text x="14" y="252" fill="#29ABE2" fontSize="16" fontWeight="800">
            For the people you love
          </text>
        </g>
      </svg>
    </Shell>
  );
};

/* =========================== Infinite Banking — loop ==================== */
export const IBMotionGraphic = () => {
  const path =
    "M 110 130 C 110 75, 180 75, 200 130 C 220 185, 290 185, 290 130 C 290 75, 220 75, 200 130 C 180 185, 110 185, 110 130 Z";
  return (
    <Shell
      headline="Borrow · Repay · Grow"
      chips={["Be your own bank", "Cash value", "Self-funding"]}
    >
      <svg viewBox="0 0 400 260" className="w-full h-auto block" aria-hidden="true">
        {sharedDefs}

        <line x1="10" y1="225" x2="390" y2="225" stroke="rgba(255,255,255,0.22)" strokeWidth="1.2" />
        <text x="14" y="252" fill="#29ABE2" fontSize="16" fontWeight="800">
          Cash flows back to you
        </text>

        <circle cx="155" cy="130" r="60" fill="url(#pmg-radial)" opacity="0.5" />
        <circle cx="245" cy="130" r="60" fill="url(#pmg-radial)" opacity="0.5" />

        <path
          d={path}
          fill="none"
          stroke="url(#pmg-line)"
          strokeWidth="7"
          strokeLinecap="round"
          opacity="0.3"
          filter="url(#pmg-glow)"
        />
        <path
          d={path}
          fill="none"
          stroke="url(#pmg-line)"
          strokeWidth="3"
          strokeLinecap="round"
          style={{ strokeDasharray: 800, strokeDashoffset: 800, animation: "iul-draw 2.2s ease-out 0.3s forwards" }}
        />

        <circle r="6" fill="#fff" filter="url(#pmg-glow)">
          <animateMotion dur="5s" repeatCount="indefinite" path={path} rotate="auto" />
        </circle>
        <circle r="4" fill="#29ABE2">
          <animateMotion dur="5s" repeatCount="indefinite" path={path} rotate="auto" />
        </circle>

        {/* node labels — bigger */}
        <g style={{ opacity: 0, animation: "iul-fade 0.8s ease-out 1.4s forwards" }}>
          <circle cx="110" cy="130" r="6" fill="#fff" />
          <text x="76" y="170" fill="#4CC3F0" fontSize="17" fontWeight="800">
            Borrow
          </text>

          <circle cx="200" cy="130" r="5" fill="#fff" />
          <text x="184" y="110" fill="#fff" fontSize="17" fontWeight="800">
            You
          </text>

          <circle cx="290" cy="130" r="6" fill="#fff" />
          <text x="261" y="170" fill="#4CC3F0" fontSize="17" fontWeight="800">
            Repay
          </text>
        </g>

        {/* flow arrows with bigger labels */}
        <g style={{ opacity: 0, animation: "iul-fade 0.8s ease-out 1.8s forwards" }}>
          <text x="135" y="72" fill="rgba(255,255,255,0.7)" fontSize="14" fontWeight="800">
            ↗
          </text>
          <text x="255" y="200" fill="rgba(255,255,255,0.7)" fontSize="14" fontWeight="800">
            ↘
          </text>
        </g>
      </svg>
    </Shell>
  );
};

/* ====================== Mortgage Protection — Home shield ================ */
export const MortgageMotionGraphic = () => {
  return (
    <Shell
      headline="Your family keeps the home"
      chips={["Home protected", "Mortgage covered", "Family secured"]}
    >
      <svg viewBox="0 0 400 260" className="w-full h-auto block" aria-hidden="true">
        {sharedDefs}

        <line x1="10" y1="225" x2="390" y2="225" stroke="rgba(255,255,255,0.22)" strokeWidth="1.2" />
        <text x="14" y="252" fill="#29ABE2" fontSize="16" fontWeight="800">
          Home, protected
        </text>

        <circle cx="200" cy="155" r="115" fill="url(#pmg-radial)" opacity="0.6" />

        {/* dome */}
        <path
          d="M 60 225 Q 200 35, 340 225"
          fill="url(#pmg-fill)"
          stroke="url(#pmg-line)"
          strokeWidth="2.8"
          strokeLinecap="round"
          filter="url(#pmg-glow)"
          style={{ strokeDasharray: 650, strokeDashoffset: 650, animation: "iul-draw 2s ease-out 0.3s forwards" }}
        />
        <path
          d="M 60 225 Q 200 35, 340 225"
          fill="none"
          stroke="url(#pmg-line)"
          strokeWidth="2.8"
          strokeLinecap="round"
          style={{ strokeDasharray: 650, strokeDashoffset: 650, animation: "iul-draw 2s ease-out 0.3s forwards" }}
        />

        {/* house */}
        <g style={{ opacity: 0, animation: "iul-fade 0.8s ease-out 1.2s forwards" }}>
          <path
            d="M 150 150 L 200 100 L 250 150 Z"
            fill="url(#pmg-line)"
            stroke="#fff"
            strokeWidth="0.9"
            strokeLinejoin="round"
            opacity="0.95"
          />
          <rect x="162" y="150" width="76" height="75" rx="2" fill="#0d2747" stroke="url(#pmg-line)" strokeWidth="1.8" />
          <rect x="192" y="190" width="16" height="35" rx="1.5" fill="#4CC3F0" opacity="0.9" />
          <rect x="170" y="162" width="14" height="14" rx="1" fill="#4CC3F0" opacity="0.55" />
          <rect x="216" y="162" width="14" height="14" rx="1" fill="#4CC3F0" opacity="0.55" />
          <rect x="220" y="118" width="10" height="14" fill="url(#pmg-line)" />
        </g>

        {/* heart above roof */}
        <g
          transform="translate(200 80)"
          style={{ opacity: 0, animation: "iul-fade 0.8s ease-out 1.6s forwards" }}
        >
          <g>
            <path
              d="M0 -3 C -7 -14, -18 -7, -18 4 C -18 14, -7 21, 0 28 C 7 21, 18 14, 18 4 C 18 -7, 7 -14, 0 -3 Z"
              fill="url(#pmg-line)"
              filter="url(#pmg-glow)"
            />
            <path
              d="M0 -3 C -7 -14, -18 -7, -18 4 C -18 14, -7 21, 0 28 C 7 21, 18 14, 18 4 C 18 -7, 7 -14, 0 -3 Z"
              fill="none"
              stroke="#fff"
              strokeWidth="0.9"
              opacity="0.6"
            />
            <animateTransform
              attributeName="transform"
              type="scale"
              values="1;1.08;1"
              dur="1.8s"
              repeatCount="indefinite"
              additive="sum"
            />
          </g>
        </g>

        {/* dome endpoint pulses */}
        <circle cx="60" cy="225" r="5" fill="#29ABE2">
          <animate attributeName="r" values="5;11;5" dur="3s" repeatCount="indefinite" />
          <animate attributeName="opacity" values="0.6;0;0.6" dur="3s" repeatCount="indefinite" />
        </circle>
        <circle cx="340" cy="225" r="5" fill="#29ABE2">
          <animate attributeName="r" values="5;11;5" dur="3s" repeatCount="indefinite" begin="1.5s" />
          <animate attributeName="opacity" values="0.6;0;0.6" dur="3s" repeatCount="indefinite" begin="1.5s" />
        </circle>
      </svg>
    </Shell>
  );
};
